// ==================================================================================
// Digital Approval System (DAS) - Report Request Detail Modal
// ==================================================================================
// Read-only detail drill-in for a report row's request number. Reuses the same
// data path as the dashboard (useRequestDetails → mapRequestDetailsToTicket) and
// renders the existing read-only CompletedTicketModal. Reports are an analytics
// surface, so no approval actions are offered here.
// ==================================================================================

import React from 'react';
import { createPortal } from 'react-dom';
import { useRequestDetails, useAuth } from 'hooks/api';
import { mapRequestDetailsToTicket } from 'api/mappers/requestToTicketMapper';
import { CompletedTicketModal } from '../../ticket-details/CompletedTicketModal';

interface ReportRequestDetailModalProps {
  isOpen: boolean;
  requestId: string | null;
  onClose: () => void;
}

const overlayStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(0,0,0,0.5)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};

const cardStyle: React.CSSProperties = {
  background: '#fff',
  borderRadius: '0.5rem',
  padding: '1.5rem 2rem',
  minWidth: 280,
  textAlign: 'center',
  boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
};

const ReportRequestDetailModal: React.FC<ReportRequestDetailModalProps> = ({
  isOpen,
  requestId,
  onClose,
}) => {
  const { user } = useAuth();
  const currentUserId = user?.id;

  const { data: requestDetails, loading, error } = useRequestDetails(isOpen ? requestId : null);

  if (!isOpen || !requestId) return null;

  // Loading / error states while the detail payload is fetched.
  if (loading || error || !requestDetails) {
    return createPortal(
      <div style={overlayStyle} onClick={onClose}>
        <div style={cardStyle} onClick={(e) => e.stopPropagation()}>
          {loading && <div style={{ color: '#6b7280' }}>Loading request…</div>}
          {!loading && (error || !requestDetails) && (
            <>
              <div style={{ color: '#991b1b', marginBottom: '1rem' }}>
                {error ? error.message : 'Request not found or not accessible.'}
              </div>
              <button
                onClick={onClose}
                style={{
                  background: '#2563eb',
                  color: '#fff',
                  border: 'none',
                  padding: '0.4rem 1rem',
                  borderRadius: '0.375rem',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </>
          )}
        </div>
      </div>,
      document.body
    );
  }

  const ticket = mapRequestDetailsToTicket(requestDetails, currentUserId);

  return <CompletedTicketModal isOpen={isOpen} onClose={onClose} ticket={ticket} />;
};

export default ReportRequestDetailModal;
